import { useCallback, useEffect, useMemo, useState } from "react";
import { KeyRound, Save, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "members", label: "Members" },
  { key: "games", label: "Games" },
  { key: "game_credits", label: "Game Credits" },
  { key: "player_requests", label: "Player Requests" },
  { key: "payment_methods", label: "Payment Methods" },
  { key: "wallet", label: "Wallet" },
  { key: "free_credits", label: "Free Credits" },
  { key: "transactions", label: "Transactions" },
  { key: "payments_review", label: "Payment Review" },
  { key: "payouts_review", label: "Payout Review" },
  { key: "depositors", label: "Depositors" },
  { key: "communications", label: "Communications" },
  { key: "support_app", label: "Support App" },
  { key: "client_referrals", label: "Client Referrals" },
];

const DEFAULT_PERMISSIONS = ["player_requests", "depositors", "communications", "support_app", "client_referrals"];

const emptyForm = {
  username: "",
  email: "",
  password: "",
  admin_permissions: DEFAULT_PERMISSIONS,
  is_active: true,
};

export default function AdminStaffUsers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/staff-users");
    setRows(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, [load]);

  const createStaff = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/staff-users", form);
      setForm(emptyForm);
      toast.success("Staff user created");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStaff = async (row) => {
    const draft = editing[row.id] || {};
    try {
      await api.put(`/admin/staff-users/${row.id}`, {
        username: draft.username ?? row.username,
        email: draft.email ?? row.email,
        password: draft.password || undefined,
        admin_permissions: draft.admin_permissions ?? row.admin_permissions,
        is_active: draft.is_active ?? row.is_active,
      });
      setEditing((current) => ({ ...current, [row.id]: { ...current[row.id], password: "" } }));
      toast.success("Staff user updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteStaff = async (row) => {
    if (!window.confirm(`Delete staff user "${row.email}"?`)) return;
    try {
      await api.delete(`/admin/staff-users/${row.id}`);
      toast.success("Staff user deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-white">Users</h1>
        <p className="mt-1 text-sm text-slate-500">Create staff logins and choose which admin pages they can use.</p>
      </div>

      <form onSubmit={createStaff} className="mb-6 rounded-lg border border-[#332a10] bg-[#101010] p-5">
        <div className="mb-4 flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-[#2F6FA5]" />
          <h2 className="text-lg font-semibold text-white">Add staff user</h2>
        </div>
        <div className="grid gap-4 lg:grid-cols-4">
          <Field label="Name">
            <input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Email">
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Password">
            <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Status">
            <label className="flex h-11 items-center gap-2 rounded-xl border border-white/10 bg-[#0d0d10] px-3 text-sm text-slate-300">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
          </Field>
        </div>
        <PermissionPicker value={form.admin_permissions} onChange={(admin_permissions) => setForm({ ...form, admin_permissions })} />
        <button disabled={saving} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60">
          <ShieldCheck className="h-4 w-4" />
          {saving ? "Creating..." : "Create staff login"}
        </button>
      </form>

      <div className="overflow-x-auto rounded-lg border border-[#332a10] bg-[#101010]">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Permissions</th>
              <th className="px-4 py-3">New password</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <StaffRow
                key={row.id}
                draft={editing[row.id] || {}}
                onDelete={() => deleteStaff(row)}
                onDraft={(patch) => setEditing((current) => ({ ...current, [row.id]: { ...current[row.id], ...patch } }))}
                onSave={() => updateStaff(row)}
                row={row}
              />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500">No staff users yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StaffRow({ draft, onDelete, onDraft, onSave, row }) {
  const permissions = draft.admin_permissions ?? row.admin_permissions ?? [];
  const active = draft.is_active ?? row.is_active;
  return (
    <tr className="border-t border-[#332a10]/70">
      <td className="px-4 py-3">
        <input value={draft.username ?? row.username} onChange={(e) => onDraft({ username: e.target.value })} className={inputClass} />
      </td>
      <td className="px-4 py-3">
        <input type="email" value={draft.email ?? row.email} onChange={(e) => onDraft({ email: e.target.value })} className={inputClass} />
      </td>
      <td className="px-4 py-3">
        <PermissionPicker compact value={permissions} onChange={(admin_permissions) => onDraft({ admin_permissions })} />
      </td>
      <td className="px-4 py-3">
        <div className="relative">
          <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input type="password" value={draft.password || ""} onChange={(e) => onDraft({ password: e.target.value })} placeholder="Leave blank" className={`${inputClass} pl-9`} />
        </div>
      </td>
      <td className="px-4 py-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={active} onChange={(e) => onDraft({ is_active: e.target.checked })} />
          {active ? "Active" : "Disabled"}
        </label>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          <button type="button" onClick={onSave} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/15">
            <Save className="h-3.5 w-3.5" /> Save
          </button>
          <button type="button" onClick={onDelete} className="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/15">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function PermissionPicker({ compact = false, onChange, value }) {
  const selected = useMemo(() => new Set(value || []), [value]);
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(PERMISSIONS.map((item) => item.key).filter((item) => next.has(item)));
  };
  return (
    <div className={`flex flex-wrap gap-2 ${compact ? "" : "mt-4"}`}>
      {PERMISSIONS.map((permission) => (
        <button
          key={permission.key}
          type="button"
          onClick={() => toggle(permission.key)}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
            selected.has(permission.key)
              ? "border-[#2F6FA5]/60 bg-[#2F6FA5]/15 text-[#B7D7F2]"
              : "border-white/10 text-slate-400 hover:bg-white/5"
          }`}
        >
          {permission.label}
        </button>
      ))}
    </div>
  );
}

function Field({ children, label }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const inputClass = "h-11 w-full rounded-xl border border-white/10 bg-[#0d0d10] px-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#2F6FA5]/70";
